import React, { Suspense, useState, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useThemeContext } from '../../../context/ThemeContext/ThemeContext';
import { useAuth } from '../../../context/AuthContext/AuthContext';
import { useAccessControl } from '../../../context/AccessControlContext/AccessControlContext';

import Loader from '../../Loader/Loader';
import Error from '../Error/Error';
import Sidebar from '../Navigation/Sidebar/Sidebar';
import Header from '../Navigation/Header/Header';
import AdminViewBanner from '../AdminViewBanner/AdminViewBanner';
import { isImpersonating } from '../../../utils/impersonationSession';

import { Box, CssBaseline, Stack, useTheme } from '@mui/material';

import classes from './_layout.module.scss';

interface LayoutProps {
  children: React.ReactNode;
}

// Si on met à 0 elle prend 100% de la width
const drawerWidth = 305;
const HEADER_HEIGHT = 100;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user: _user, profile } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isDarkMode: _isDarkMode, toggleTheme: _toggleTheme } = useThemeContext();
  const theme = useTheme();
  const { accessGranted } = useAccessControl();
  const { t, i18n } = useTranslation('accessDenied');
  const isRtl = i18n.language === 'ar';

  const navigate = useNavigate();
  const location = useLocation();

  // Détecte si on est en version mobile au premier rendu en fonction de la largeur de l'écran
  const isMobile = window.innerWidth < 960; // Utilise 960px comme seuil pour la version mobile

  // Initialisation de l'état de `open` : fermé par défaut en mobile, ouvert en desktop
  const [open, setOpen] = useState(!isMobile);

  // Vérifie si le bandeau admin est visible
  const isAdminViewBannerVisible = isImpersonating();
  const bannerHeight = isAdminViewBannerVisible ? 48 : 0;

  // Ajout de l'événement 'resize' pour mettre à jour `open` en fonction de la largeur de l'écran
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 960) {
        setOpen(false); // Ferme la Sidebar en mobile
      } else {
        setOpen(true); // Ouvre la Sidebar en desktop
      }
    };

    // Attache l'événement 'resize'
    window.addEventListener('resize', handleResize);

    // Nettoie l'événement lors du démontage du composant
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Le tableau vide [] garantit que l'effet est exécuté une seule fois

  const handleDrawerOpen = () => {
    setOpen(!open); // Ouvre / ferme la Sidebar manuellement
  };

  const handleDrawerClose = () => {
    setOpen(false); // Ferme la Sidebar manuellement
  };

  if (!accessGranted) {
    return (
      <Error
        documentTitle={t('documentTitle')}
        title={t('title')}
        subtitle={t('text')}
        button={t('button')}
        onClick={() => navigate('/')}
      />
    );
  }

  // Bypass the full layout (sidebar, header) for the SSO bridge page.
  // The SSO flow replaces the current session, so the authenticated chrome must not render.
  // The application funnel (/apply) is a standalone full-screen page, with or without a session.
  if (location.pathname === '/sso' || location.pathname === '/apply') {
    return (
      <>
        <CssBaseline />
        <Suspense fallback={<Loader />}>
          {children}
        </Suspense>
      </>
    );
  }

  // Bypass the full layout (sidebar, header) for first-time users on /buy-funded.
  // This cannot easily be moved to Router.tsx because Layout wraps all routes in App.tsx.
  const isFirstTimeUser = !profile?.hasFirstTimeDeposit && !profile?.hasActiveInstantFunded;
  if (isFirstTimeUser && location.pathname === '/buy-funded') {
    return (
      <>
        <CssBaseline />
        <Suspense fallback={<Loader />}>
          {children}
        </Suspense>
      </>
    );
  }

  // The chrome (banner, header, sidebar) can suspend too: translations of its namespaces load
  // lazily, and a client-side navigation from a standalone page (/apply, first /buy-funded) into
  // the full layout renders it for the first time inside a synchronous click. Without a boundary
  // above it, React 18 aborts the update with error #426 and leaves a blank page.
  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Suspense fallback={<Loader />}>
        <AdminViewBanner />
        <Stack
          sx={{
            order: isRtl ? 1 : 0,
            transition: theme.transitions.create('order', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <CssBaseline />
          <Header
            open={open}
            drawerWidth={drawerWidth}
            handleDrawerOpen={handleDrawerOpen}
            bannerHeight={bannerHeight}
            isRtl={isRtl}
          />
          <Sidebar open={open} handleDrawerClose={handleDrawerClose} bannerHeight={bannerHeight} isRtl={isRtl} />
        </Stack>
      </Suspense>
      <main
        className={location.pathname === '/login' ? '' : classes.main}
        style={{
          paddingTop: location.pathname !== '/login' ? `calc(${HEADER_HEIGHT}px + ${bannerHeight}px)` : undefined,
          transition: theme.transitions.create(['margin-left', 'margin-right'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(location.pathname === '/webtrader' && {
            paddingTop: `${64 + bannerHeight}px`,
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: 0,
            height: '100vh',
            boxSizing: 'border-box' as const,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const,
          }),
          ...(location.pathname.startsWith('/admin') && {
            paddingLeft: '24px',
            paddingRight: '24px',
          }),
        }}
      >
        <Suspense fallback={<Loader />}>
          {children}
        </Suspense>
      </main>
    </Box>
  );
};

export default Layout;
